import { BlobWithFilename, isBlobWithFilename } from 'apiConnector/document/document';
import { MultiLanguageDocument } from 'components/shared/Document/MultiLanguageDocument';
import { DocumentFormat } from 'epic/common-form/Documentation';
import { useCrossBrowserContext } from 'hooks/useCrossBrowserContext';
import type { IntlKey } from 'locales';
import { first } from 'lodash-es';
import type { WorkspaceAnalyticCategory } from 'models/analytics';
import type { DocumentType } from 'models/documents/DocumentType';
import { FC, useCallback, useContext } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import { downloadFile } from 'utils/documents/downloadDocument';
import { sanitizeNonAlphaChars } from 'utils/string';

import { useGenerateDocument } from '../documents/DocumentButton.helper';
import type { DocumentProps } from './DocumentButton.connect.legacy';

export const RawDocumentButton: FC<DocumentProps> = ({
  languages,
  quoteQuery,
  documentType,
  isEnabled,
  className,
  errorContext,
  onError,
  onSuccess,
  onDownloadStart,
  productSubtype,
  quoteId,
  quoteState,
  productType,
}) => {
  const { isDirty, isLocked } = useCrossBrowserContext();
  const isCrossBrowserFrozen = isDirty || isLocked;
  const isDocumentEnabled = isEnabled && !isCrossBrowserFrozen;
  const generateDocument = useGenerateDocument(
    quoteQuery,
    documentType,
    productSubtype,
    quoteId,
    quoteState,
    productType,
  );
  const category = useContext(WorkspaceAnalyticCategoryContext);
  const handleDownloadResult = useCallback((result: Blob | BlobWithFilename, fallbackFilename?: string) => {
    if (isBlobWithFilename(result)) {
      return downloadFile(result.blob, result.filename ?? fallbackFilename);
    }
    return downloadFile(result, fallbackFilename);
  }, []);

  const onDownload = useCallback(
    (local: string, documentFormat: DocumentFormat) => {
      const matchingLanguages = languages.filter(l => l.language === local);
      const fileName = (matchingLanguages.find(l => l.documentFormat === documentFormat) ?? first(matchingLanguages))
        ?.fileName;

      onDownloadStart(local, documentFormat);

      generateDocument(local, documentFormat)
        .then(result => handleDownloadResult(result, fileName))
        .then(() => onSuccess(local, documentFormat))
        .catch(err => onError(local, err, documentFormat));

      trackDownload(documentType, category);
    },
    [handleDownloadResult, generateDocument, languages, documentType, category, onSuccess, onDownloadStart, onError],
  );

  if (languages.length === 0 || !isDocumentEnabled) {
    return null;
  }

  return (
    <MultiLanguageDocument
      id={`downloadTermsheet-error-icons-${sanitizeNonAlphaChars(documentType)}`}
      labelKey={`form.buttons.${documentType}Button` as IntlKey}
      label={documentType}
      className={className}
      languages={languages}
      isEnabled={isDocumentEnabled}
      e2e={getE2EString(documentType)}
      onDocumentDownload={onDownload}
      errorContext={errorContext}
    />
  );
};

function getE2EString(documentType: DocumentType | string): string {
  switch (documentType) {
    case 'Termsheet':
    case 'TermSheet':
      return 'download-termsheet';
    case 'MarketingMaterial':
      return 'download-marketing-material';
    case 'FinalTerms':
      return 'download-final-terms';
    case 'Kid':
      return 'download-kid';
    case 'InformationFactSheet':
      return 'download-information-fact-sheet';
    case 'SupplementDistributionAgreement':
      return 'download-supplement-distribution-agreement';
    default:
      return 'download-document';
  }
}

function trackDownload(documentType: DocumentType | string, category: WorkspaceAnalyticCategory) {
  switch (documentType) {
    case 'Termsheet':
    case 'TermSheet':
      logAnalytics.trackEvent(category, 'Download termsheet');
      break;
    case 'MarketingMaterial':
      logAnalytics.trackEvent(category, 'Download marketing material');
      break;
    case 'Brochure':
      logAnalytics.trackEvent(category, 'Download brochure');
      break;
    case 'FinalTerms':
      logAnalytics.trackEvent(category, 'Download final terms');
      break;
    case 'InformationFactSheet':
      logAnalytics.trackEvent(category, 'Download information fact sheet');
      break;
    case 'SupplementDistributionAgreement':
      logAnalytics.trackEvent(category, 'Download supplement distribution agreement');
      break;
    default:
      break;
  }
}

RawDocumentButton.displayName = 'RawDocumentButton';
